import { useState, useEffect, useCallback, useRef } from 'react'
import { propertiesService } from '../services/propertiesService'
import { useAuth } from '../contexts/AuthContext'
import { propertyValuationCacheService } from '../services/propertyValuationCacheService'

const EMPTY_PAGINATION = { page: 1, limit: 100, total: 0, pages: 0, hasMore: false }
const EMPTY_COUNTS = { total: 0, active: 0, completed: 0 }

export const useProperties = ({ offerType, type, region, municipalities, districts, layouts, displayOptions, sort, priceRange, search = '' }) => {
  const { authenticated } = useAuth()
  const [properties, setProperties] = useState(null)
  const [allDistricts, setAllDistricts] = useState([])
  const [pagination, setPagination] = useState(EMPTY_PAGINATION)
  const [counts, setCounts] = useState(EMPTY_COUNTS)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [loadMoreError, setLoadMoreError] = useState(null)
  const [error, setError] = useState(null)
  const requestIdRef = useRef(0)
  const loadingMoreRef = useRef(false)

  const districtsKey = districts.join(',')
  const municipalitiesKey = municipalities.join(',')
  const layoutsKey = layouts.join(',')
  const displayOptionsKey = displayOptions.join(',')
  const minPrice = priceRange?.min || 0
  const maxPrice = priceRange?.max ?? 30000000

  const getRequest = useCallback(
    (page) => ({
      offerType,
      type,
      region,
      page,
      limit: 100,
      sort,
      districts,
      municipalities,
      layouts,
      displayOptions,
      priceRange: { min: minPrice, max: maxPrice },
      search,
    }),
    // Stable string keys avoid restarting the request for equivalent arrays.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [offerType, type, region, sort, municipalitiesKey, districtsKey, layoutsKey, displayOptionsKey, minPrice, maxPrice, search]
  )

  const fetchFirstPage = useCallback(async () => {
    const requestId = ++requestIdRef.current
    loadingMoreRef.current = false

    if (!authenticated) {
      setProperties(null)
      setAllDistricts([])
      setPagination(EMPTY_PAGINATION)
      setCounts(EMPTY_COUNTS)
      setLoading(false)
      setLoadingMore(false)
      setLoadMoreError(null)
      return
    }

    try {
      setLoading(true)
      setLoadingMore(false)
      setLoadMoreError(null)
      setError(null)
      setProperties(null)
      setAllDistricts([])
      setPagination(EMPTY_PAGINATION)
      setCounts(EMPTY_COUNTS)

      const data = await propertiesService.fetchProperties(getRequest(1))
      if (requestId !== requestIdRef.current) return

      setProperties(data.properties || [])
      setAllDistricts(data.availableDistricts || [])
      setPagination(data.pagination || EMPTY_PAGINATION)
      setCounts(data.counts || EMPTY_COUNTS)
    } catch (err) {
      if (requestId !== requestIdRef.current) return
      console.error('Error fetching properties:', err)
      setError(err)
      setProperties([])
    } finally {
      if (requestId === requestIdRef.current) setLoading(false)
    }
  }, [authenticated, getRequest])

  useEffect(() => {
    fetchFirstPage()
  }, [fetchFirstPage])

  const pendingValuationIds = (properties || [])
    .filter((property) => {
      const preview = property.valuationPreview
      return preview && (preview.refreshNeeded || preview.refreshing || preview.status === 'pending' || preview.status === 'calculating')
    })
    .map((property) => property.propertyId)
    .slice(0, 500)
  const pendingValuationIdsKey = pendingValuationIds.join(',')

  useEffect(() => {
    if (!authenticated || !pendingValuationIdsKey) return undefined
    let cancelled = false
    let requestRunning = false
    const pollValuations = async () => {
      if (requestRunning) return
      requestRunning = true
      try {
        const data = await propertyValuationCacheService.fetchBatch(pendingValuationIdsKey.split(','))
        if (!cancelled) {
          setProperties((current) =>
            (current || []).map((property) =>
              data.properties?.[property.propertyId]
                ? { ...property, valuationPreview: data.properties[property.propertyId] }
                : property
            )
          )
        }
      } catch (error) {
        if (!cancelled) console.error('Error refreshing property valuation previews:', error)
      } finally {
        requestRunning = false
      }
    }
    const firstPollId = window.setTimeout(pollValuations, 1500)
    const intervalId = window.setInterval(pollValuations, 3500)
    return () => {
      cancelled = true
      window.clearTimeout(firstPollId)
      window.clearInterval(intervalId)
    }
  }, [authenticated, pendingValuationIdsKey])

  const loadMore = useCallback(async () => {
    if (!authenticated || loading || loadingMoreRef.current || !pagination.hasMore) return

    const requestId = requestIdRef.current
    const nextPage = pagination.page + 1
    loadingMoreRef.current = true
    setLoadingMore(true)
    setLoadMoreError(null)

    try {
      const data = await propertiesService.fetchProperties(getRequest(nextPage))
      if (requestId !== requestIdRef.current) return

      setProperties((current) => {
        const existing = new Set((current || []).map((property) => property.propertyId))
        const newProperties = (data.properties || []).filter((property) => !existing.has(property.propertyId))
        return [...(current || []), ...newProperties]
      })
      setPagination(data.pagination || EMPTY_PAGINATION)
      setCounts(data.counts || EMPTY_COUNTS)
    } catch (err) {
      if (requestId === requestIdRef.current) {
        console.error('Error fetching more properties:', err)
        setLoadMoreError(err)
      }
    } finally {
      if (requestId === requestIdRef.current) {
        loadingMoreRef.current = false
        setLoadingMore(false)
      }
    }
  }, [authenticated, getRequest, loading, pagination.hasMore, pagination.page])

  return {
    properties,
    allDistricts,
    pagination,
    counts,
    loading,
    loadingMore,
    loadMoreError,
    error,
    loadMore,
    refetch: fetchFirstPage,
  }
}
